export const strings = {
    app: {
        name: 'Mackinac',
    },
    common: {
        loadingMore: 'Loading more…',
        back: 'Back',
        cancel: 'Cancel',
        confirm: 'Confirm',
        save: 'Save',
        close: 'Close',
        edit: 'Edit',
        delete: 'Delete',
        descriptionLabel: 'Description',
    },
    protocols: {
        atProtocol: 'AT Protocol',
        fediverse: 'Fediverse',
        nostr: 'Nostr',
    },
    apps: {
        bluesky: 'Bluesky',
        blacksky: 'Blacksky',
        mastodon: 'Mastodon',
        pixelfed: 'Pixelfed',
    },
    header: {
        signOut: 'Sign out',
        userAvatarAlt: 'User avatar',
        addAccount: 'Add account',
    },
    login: {
        welcomeHeading: 'Welcome to Mackinac',
        connectHeading: 'Connect account',
        tagline: 'Your bridge to the open social web',
        connectSubtitle: 'Add another network to your profile',
        connectAccountHeading: (name: string) => `Connect your ${name} account`,
        signInHeading: (name: string) => `Sign in with ${name}`,
        back: 'Back',
        connect: 'Connect',
        signIn: 'Sign in',
        redirecting: 'Redirecting...',
        add: 'Add',
        newHere: 'New here?',
        signUp: 'Sign up',
        signUpAriaLabel: (name: string) => `Sign up for ${name} (opens in new tab)`,
        whatIsAtmosphere: 'What is Atmosphere?',
        atmosphereExplainer:
            'Atmosphere is an open social network. Your account works across many apps, including Bluesky.',
        mastodonPlaceholder: 'e.g. mastodon.social',
        nostrNip07: 'NIP-07 extension',
        nostrNip07Helper: 'Alby, nos2x',
        nostrNsecLabel: 'Nostr secret key (nsec)',
        nostrNsecPlaceholder: 'nsec1...',
        nostrConnectNsec: 'Connect with nsec',
        previousHandles: 'Previously used',
        removeHandle: (handle: string) => `Remove ${handle} from history`,
        appViewLabel: 'App view',
        userHandleLabel: 'User handle',
        logoAlt: 'Mackinac logo',
        expiredSection: 'Signed out',
        expiredSubtitle: 'Signed out — tap to reconnect',
        reconnectAriaLabel: (handle: string) => `Reconnect ${handle}`,
        reconnectFailed: 'Could not reconnect. Please try again.',
        reconnectMismatch:
            'That sign-in was for a different account, so the expired account is still signed out.',
        dismissError: 'Dismiss',
        accountLimitReached:
            'You have reached the account limit. Reconnect or remove an account to add another.',
        removeExpiredAriaLabel: (handle: string) => `Remove ${handle}`,
        accountWithUnreadAriaLabel: (displayName: string, count: string) =>
            `${displayName}, ${count} unread`,
        removeExpiredTitle: (handle: string) => `Remove ${handle}?`,
        removeExpiredBody:
            'This account is signed out. Removing it deletes it from this device. You can sign in again later.',
        removeExpiredConfirm: 'Remove',
        errors: {
            enterHandle: 'Please enter a handle',
            enterInstanceUrl: 'Please enter an instance URL',
            loginFailed: 'Login failed. Please check your input and try again.',
            extensionFailed: 'Failed to connect via extension.',
            enterNsec: 'Please enter your nsec key.',
            invalidNsec: 'Invalid nsec key.',
        },
        terms: 'Terms',
        privacy: 'Privacy',
        legalSeparator: '·',
        agreementNoticePrefix: 'By continuing, you agree to our',
        agreementNoticeConjunction: 'and',
    },
    nostrReauth: {
        title: 'Re-enter your Nostr key',
        body: 'Your signing key needs to be re-entered to continue posting. This happens when the app restarts.',
        inputPlaceholder: 'nsec1...',
        submitLabel: 'Restore key',
        submittingLabel: 'Restoring...',
        cancelLabel: 'Cancel',
        invalidKey: 'Invalid nsec key. Please try again.',
        successToast: 'Key restored — please try again',
    },
    bridgeAP: {
        buttonLabelEnable: 'Bridge to Atmosphere',
        buttonLabelDisable: 'Disconnect from Atmosphere',
        buttonAriaNotBridged: 'Bridge to Atmosphere',
        buttonAriaActive: 'Atmosphere bridge active',
        buttonAriaPending: 'Atmosphere bridge setup in progress',
        tooltipNotBridged: 'Connect to the Atmosphere',
        tooltipBridged: 'Connected to the Atmosphere',
        tooltipPending: 'Connecting to the Atmosphere...',
        enableModalTitle: 'Bridge to Atmosphere',
        enableModalBody: 'Your posts will be available on apps like Bluesky in the Atmosphere.',
        enableModalAtmosphereHandle: 'Your Atmosphere handle will be',
        enableModalDisclaimerCanDisable: 'You can disconnect at any time.',
        enableModalFollowNotice: 'This will follow',
        enableModalFollowNoticeSuffix: 'on your behalf to enable the bridge.',
        enableModalLearnMore: 'Learn more at fed.brid.gy',
        enableConfirmLabel: 'Bridge account',
        cancelLabel: 'Cancel',
        bridgedModalTitle: 'Connected to the Atmosphere',
        bridgedModalAtmosphereHandle: 'Your Atmosphere handle is',
        bridgedModalBody:
            'Bridgy Fed bridges your posts to the Atmosphere, allowing Atmosphere users to discover and interact with your content.',
        bridgedModalBridgyFedWebsite: 'Bridgy Fed website',
        bridgedModalDocs: 'Documentation',
        disableConfirmButtonInBridgedModal: 'Disconnect from Atmosphere',
        closeLabel: 'Close',
        pendingModalTitle: 'Bridge setup in progress',
        pendingModalBody:
            'Your account is being bridged to the Atmosphere. This usually takes a few minutes.',
        pendingModalAtmosphereHandle: 'Your Atmosphere handle will be',
        disableModalTitle: 'Disconnect from Atmosphere',
        disableModalBody:
            'Your posts will no longer appear on apps like Bluesky in the Atmosphere.',
        disableModalBlockNotice: 'This will block',
        disableModalBlockNoticeSuffix: 'on your behalf.',
        disableConfirmLabel: 'Disconnect',
        enableErrorFederationNotFound:
            "Your Mastodon server hasn't seen the Atmosphere bridge yet. Try searching for `@bsky.brid.gy@bsky.brid.gy` on your instance first to federate it.",
        enableErrorPollTimeout:
            'Bridge setup is taking longer than expected. Please refresh the page to check status.',
        enableErrorPollFailed:
            'Bridge setup failed while verifying the connection. Please try again.',
        pendingFederationModalTitle: 'Bridge request sent',
        pendingFederationModalBody:
            'Bridge request sent — waiting for the Atmosphere to accept (this usually takes a few minutes). Check your Mastodon pending follow requests.',
        pendingFederationTooltip: 'Bridge request pending — waiting for Atmosphere to accept',
        buttonAriaPendingFederation: 'Atmosphere bridge request pending',
        bridgeRejectedModalTitle: 'Bridge declined',
        bridgeRejectedMessage:
            "The Atmosphere bridge declined the connection. This can happen if your account is blocked on the bridge, if a previous bridge attempt is still pending cleanup, or if your AT Protocol side needs setup. Check https://fed.brid.gy for your account's bridge status.",
        bridgeRejectedAriaLabel: 'Bridge attempt declined — try again',
        bridgeRejectedTooltip: 'Bridge declined — click to retry',
        bridgeRejectedMessagePrefix: 'Bridgy Fed said:',
        seeOriginalMessage: 'See original message',
        bridgeDeclinedToast: 'Bridge declined. Click the globe button for details.',
        bridgedDelayNotice:
            'After connecting or disconnecting, there may be a short delay before your bridged profile updates across networks.',
        welcomeMessagePrefix: 'Message from Bridgy Fed:',
        bridgedModalIntro: 'Your account is connected to the Atmosphere via Bridgy Fed (',
    },
    /** AT→AP (Atmosphere-side) bridge flow; mirrors mobile `bridgeAccount`. */
    bridgeAT: {
        notBridgedModalTitle: 'Bridging to the Fediverse',
        notBridgedBody:
            'Bridge your Atmosphere account to the Fediverse via Bridgy Fed. Your posts will be visible on Mastodon and other ActivityPub platforms.',
        disclaimerCanDisable: 'You can disable this at any time.',
        enableButtonLabel: 'Enable bridge',
        bridgedModalTitle: 'Connected to the Fediverse',
        bridgedModalIntro: 'Your account is connected to the Fediverse via Bridgy Fed (',
        bridgyFedPageLink: 'Your Bridgy Fed page',
        disableButtonLabel: 'Disable bridge',
        pendingModalTitle: 'Bridge setup in progress',
        pendingBody:
            'Your account is being bridged to the Fediverse. This usually takes a few minutes.',
        disableConfirmTitle: 'Disable bridging to the Fediverse',
        disableModalBody:
            'This will remove your bridged Fediverse profile and your posts will no longer be visible on Mastodon.',
    },
    bridgeAccountSection: {
        sectionHeader: 'ActivityPub bridge',
        notBridgedDescription: 'Make your account visible on Mastodon and the Fediverse',
        bridgeButtonLabel: 'Bridge to ActivityPub',
        pendingNotice: 'Bridge activation is in progress and may take a few minutes.',
        bridgedDescription: 'Bridged to the Fediverse via Bridgy Fed',
        disableButtonLabel: 'Disable bridge',
        enableModalTitle: 'Bridge your account to the Fediverse',
        enableConfirmLabel: 'Bridge account',
        enableModalBody: 'Your Bluesky posts will be visible on Mastodon and the Fediverse.',
        disableModalTitle: 'Disable ActivityPub bridge',
        disableModalBody:
            'This will remove your bridged Fediverse profile and your posts will no longer be visible on Mastodon.',
        successTitle: 'Bridge enabled',
        successDoneButtonLabel: 'Done',
    },
    profile: {
        editProfile: 'Edit profile',
        followers: 'followers',
        subscribers: 'subscribers',
        following: 'following',
        posts: 'posts',
        viewMore: 'View more',
        moreLinks: (hidden: number) => `+${hidden} more`,
        loadingAlt: 'Loading',
        bannerAlt: 'Profile banner',
        addExtendedBio: 'Add extended bio',
        addLinks: 'Add links',
        saveChanges: 'Save changes',
        displayNameLabel: 'Display name',
        /** Accessible name + tooltip of the rel="me" verified-link badge (#2678). */
        verifiedLink: 'Verified link',
        addList: 'Add list',
        noListsYet: 'No lists yet',
        createAListButton: 'Create a list',
        createListTitle: 'Create list',
        createListConfirm: 'Create',
        listTitleLabel: 'Title',
        listTitlePlaceholder: 'List title',
        deleteListTitle: 'Delete list',
        deleteListConfirm: 'Delete',
        deleteListBody: (title: string) =>
            `Are you sure you want to delete "${title}"? This action cannot be undone.`,
        failedToLoad: 'Failed to load profile',
        locationLabel: 'Location',
        pronounsLabel: 'Pronouns',
        descriptionPlaceholder: 'Tell us about yourself',
        displayNamePlaceholder: 'Your display name',
        postComposer: {
            placeholder: "What's on your mind?",
            post: 'Post',
        },
        tabs: {
            posts: 'Posts',
            replies: 'Replies',
            likes: 'Likes',
            reposts: 'Reposts',
            boosts: 'Boosts',
            media: 'Media',
            videos: 'Videos',
            lists: 'Lists',
            saved: 'Saved',
            articles: 'Articles',
            publications: 'Publications',
            verifications: 'Verifications',
            feeds: 'Feeds',
        },
        noPublicationsYet: 'No publications yet',
    },
    compose: {
        youAvatarAlt: 'You',
        quotePlaceholder: 'Add your thoughts',
        replyPlaceholder: 'Write your reply',
        keepEditing: 'Keep editing',
        discard: 'Discard',
        cwNone: 'None',
        cwLabels: {
            nudity: 'Nudity',
            sexualContent: 'Sexual content',
            graphicMedia: 'Graphic media',
            explicit: 'Explicit',
        },
        contentWarningTitle: 'Content warning',
        removeContentWarningTitle: 'Remove content warning',
        changeLanguageTitle: 'Change language',
        removeLanguageTitle: 'Remove language',
        setLanguageTitle: 'Set language',
        visibility: {
            public: 'Public',
            unlisted: 'Unlisted',
            followersOnly: 'Followers only',
            direct: 'Direct',
        },
    },
    extendedBio: {
        title: 'About',
        editTitle: 'Edit about',
        saved: 'Extended bio saved!',
        saveFailed: 'Failed to save extended bio',
        mastodonReconnect: 'Reconnect your Mastodon account to save your extended bio',
        mastodonRateLimited: 'Too many save attempts. Try again later',
        mastodonUnavailable: 'Mastodon extended bios are temporarily unavailable',
        addLink: 'Add link',
        urlMustBeHttp: 'URL must start with http:// or https://',
        textFieldsTitle: 'Text fields',
        newLinkTitle: 'New link',
        linkTitlePlaceholder: 'Link title',
        add: 'Add',
        linksHeading: 'Links',
        nameLabel: 'Name',
        platformLabel: 'Platform',
        bioContentPlaceholder: 'Bio content...',
        headingPlaceholder: 'Heading',
    },
    profileLinks: {
        onBluesky: 'On Bluesky',
        mackinacOnly: 'Mackinac only',
        alsoOnBluesky: 'Also on Bluesky',
        publishAction: 'Add my Mackinac links to Bluesky',
        publishConfirm:
            'These links will become public on Bluesky and are subject to Bluesky moderation.',
        publishConfirmAction: 'Add to Bluesky',
        reauthorize: 'Re-authorize to sync links to Bluesky',
        httpsOnly: 'Bluesky links must start with https://',
        shortenerRejected: 'Bluesky does not allow shortened links',
        duplicateRejected: 'That link is already on Bluesky',
        titleTooLong: 'Bluesky link titles can be at most 40 characters',
        maxLinks: 'Bluesky profiles can show at most 10 links',
        saved: 'Bluesky link saved',
        deleted: 'Bluesky link removed',
        reordered: 'Bluesky links reordered',
        published: 'Mackinac links added to Bluesky',
        nothingToPublish: 'Those links are already on Bluesky',
        saveFailed: 'Failed to save Bluesky link',
        deleteFailed: 'Failed to remove Bluesky link',
        reorderFailed: 'Failed to reorder Bluesky links',
        publishFailed: 'Failed to add links to Bluesky',
        skipInsecure: 'Skipped because Bluesky only accepts https links',
        skipShortener: 'Skipped because Bluesky hides shortened links',
        skipInvalid: 'Skipped because the URL is not a usable link',
        skipCap: 'Skipped because Bluesky profiles show at most 10 links',
        skipDuplicate: 'Already on Bluesky',
        destinationLabel: 'Where should this link appear?',
        destinationBluesky: 'On Bluesky',
        destinationMackinac: 'Mackinac only',
        editLink: 'Edit link',
        reorderLink: 'Reorder link',
        reorderLabel: (name: string) => `Reorder ${name}`,
        urlLabel: 'URL',
        skippedHeading: 'Some links were not added',
        savesImmediatelyHint:
            'Adding or removing a link saves right away. Cancel only discards a reorder you have not saved.',
        deleteConfirmTitle: 'Remove link?',
        deleteConfirmBody: 'This link will be removed from your profile.',
        deleteConfirmAction: 'Remove',
        deleteConfirmCancel: 'Cancel',
        deletePartialFailed:
            'The link was removed from Bluesky but not from Mackinac. Remove it again to finish.',
        reorderNotSaved:
            'Your link order was not saved, so nothing was changed. Your other edits are still here. Try saving again.',
        saveWithoutReorder: 'Save without the new order',
        maxLinksReached: 'You have reached the maximum number of links. Remove one to add another.',
        deleteConfirmBothCopies:
            'This link is on both your Bluesky and Mackinac profiles, so it will be removed from both.',
        removing: 'Removing\u2026',
    },
    profileLabels: {
        detailTitle: 'Label details',
        unknownLabeler: 'Unknown labeler',
        expiresLabel: 'Expires',
        issuedLabel: 'Issued',
        labelValueLabel: 'Label',
    },
    viewProfile: {
        header: {
            moreActionsAriaLabel: 'More actions',
            addToList: 'Add to list',
            followsYou: 'Follows you',
            verifiedByYou: 'Verified by you',
            verify: 'Verify',
            blockedNotice: 'You have blocked this user.',
            blockConfirmLabel: 'Block',
            blockConfirmTitle: (handle: string) => `Block @${handle}?`,
            blockConfirmBody: 'They will not be able to see your posts or interact with you.',
        },
    },
    verification: {
        myVerifications: 'My verifications',
        hideAll: 'Hide verifications',
        hideAllDescription: 'Hide all verification badges across the app',
        custom: 'Custom verification',
        customDescription:
            "Choose your own trusted verifiers instead of relying on the appview's defaults",
        customPerformanceNote:
            'Turning this on makes extra network requests for every author you see and can slow scrolling on slower devices or connections.',
        enableCustomAriaLabel: 'Enable custom verification',
        trustedVerifiers: 'Trusted verifiers',
        noTrustedVerifiers: 'No trusted verifiers configured',
        addVerifiersHint:
            'You can add verifiers by visiting a profile and using the "Trust as verifier" button.',
        resetToDefaults: 'Reset to defaults',
        trustedVerifier: 'Trusted verifier',
        verifying: 'Verifying...',
        revoking: 'Revoking...',
        trustAsVerifier: 'Trust as verifier',
        verifiedAccountTitle: 'Verified account',
        learnMoreLink: 'Learn more about AT Protocol identity',
        viaBridgeExplanation: 'This account is verified on the AT Protocol (Bluesky).',
        verifiedByTrustedVerifierExplanation:
            'This account has been verified by a trusted verifier.',
        verifiedByYouExplanation: 'You verified this account.',
        verifiedByHeading: 'Verified by',
        issuedList: {
            cancel: 'Cancel',
            failedToLoad: 'Failed to load verifications.',
            noneYet: 'No verifications issued yet.',
        },
    },
    crossProtocol: {
        accountsInFeed: 'Accounts in feed',
    },
    avatar: {
        removePhoto: 'Remove avatar',
        uploadPhoto: 'Upload photo',
        cancel: 'Cancel',
        photoAriaLabel: 'Profile photo',
        fileTooLarge: 'File is too large to process',
        invalidType: 'Only JPEG, PNG, and WebP images are allowed',
        uploadFailed: 'Failed to upload profile photo',
        viewAriaLabel: 'View profile photo',
        closeAriaLabel: 'Close profile photo',
        enlargedAriaLabel: 'Enlarged profile photo',
    },
    posts: {
        repost: 'Repost',
        undoRepost: 'Undo repost',
        boost: 'Boost',
        undoBoost: 'Undo boost',
        quote: 'Quote',
        reply: 'Reply',
        reposted: 'reposted',
        youReposted: 'You reposted',
        boosted: 'boosted',
        youBoosted: 'You boosted',
        repostOptions: 'Repost options',
        sharePost: 'Share post',
        bookmark: 'Bookmark post',
        removeBookmark: 'Remove bookmark',
        pinned: 'Pinned',
        /** Followed-hashtag attribution banner on a Following-timeline post. */
        viewHashtagAriaLabel: (tag: string) => `View #${tag} hashtag`,
        moreHashtags: (count: number) => `+${count} more`,
        pinToProfile: 'Pin to profile',
        unpinFromProfile: 'Unpin from profile',
        postPinned: 'Post pinned',
        postUnpinned: 'Post unpinned',
        couldNotPin: 'Could not pin post',
        couldNotUnpin: 'Could not unpin post',
        pinPendingSync: 'Pinned — your profile will update shortly',
        unpinPendingSync: 'Unpinned — your profile will update shortly',
        pinRejected:
            "This post couldn't be pinned. It may not be eligible, or your instance's pinned-post limit may have been reached.",
        pinOutOfDate:
            "This post isn't pinned anymore — a different post was pinned somewhere else. Nothing was changed.",
        linkCopied: 'Link copied!',
        couldNotCopyLink: 'Could not copy link',
        copyText: 'Copy text',
        textCopied: 'Text copied!',
        couldNotCopyText: 'Could not copy text',
        copyUri: 'Copy URI',
        uriCopied: 'URI copied!',
        couldNotCopyUri: 'Could not copy URI',
        openInAtExplore: 'Open in AT Explore',
        showMoreLikeThis: 'Show more like this',
        showLessLikeThis: 'Show less like this',
        feedbackSent: 'Feedback sent!',
        couldNotSendFeedback: 'Could not send feedback',
        couldNotFollow: 'Could not follow. Please try again.',
        failedToLoad: 'Failed to load posts',
        noPostsYet: 'No posts yet',
        notFound: 'Post not found',
        threadNotFound: 'Thread not found',
        deleteTitle: 'Delete post?',
        deleteConfirm: 'Delete',
        deleteBody: "This can't be undone. The post will be permanently removed.",
        followConfirmLabel: 'Follow',
        repostsLabel: (count: number) => (count === 1 ? 'repost' : 'reposts'),
        likesLabel: (count: number) => (count === 1 ? 'like' : 'likes'),
        boostsLabel: (count: number) => (count === 1 ? 'boost' : 'boosts'),
        favoritesLabel: (count: number) => (count === 1 ? 'favorite' : 'favorites'),
        repliesLabel: (count: number) => (count === 1 ? 'reply' : 'replies'),
        /** Accessible name for the read-only like/repost/reply counts row on a post card. */
        engagementCounts: 'Post engagement counts',
    },
    engagementList: {
        likesTitle: 'Likes',
        repostsTitle: 'Reposts',
        favoritesTitle: 'Favorites',
        boostsTitle: 'Boosts',
        quotesTitle: 'Quotes',
    },
    articles: {
        pageTitle: 'Articles',
        disableInAppReaderLabel: 'Disable in-app reader',
        disableInAppReaderDescription:
            "Opens articles directly on the publisher's website instead of the in-app reader.",
        moreOptions: 'More options',
        copyUri: 'Copy URI',
        openInAtExplore: 'Open in AT Explore',
        uriCopied: 'URI copied!',
        couldNotCopyUri: 'Could not copy URI',
        readInMackinac: 'Read in Mackinac',
        viewPublication: 'View publication',
        readOnPublication: (host: string) => `Read on ${host} →`,
        viewOriginal: 'View',
        shareOriginal: 'Share',
        originalUrlCopied: 'Original link copied!',
        couldNotCopyOriginalUrl: 'Could not copy original link',
        minRead: (minutes: number) => `${minutes} min read`,
        rssSourceFallback: 'RSS feed',
        rssBadge: 'RSS',
        featuredByline: (name: string) => `By ${name}`,
    },
    searchSettings: {
        pageTitle: 'Search',
        hideTopStoriesLabel: 'Hide top stories',
        hideTopStoriesDescription: 'Hide the top stories section on the search page.',
    },
    publications: {
        editTitle: 'Edit publication',
        uriCopied: 'URI copied!',
        couldNotCopyUri: 'Could not copy URI',
        iconLabel: 'Icon',
        iconPreviewAlt: 'Publication icon preview',
        nameLabel: 'Name',
        namePlaceholder: 'Publication name',
        descriptionPlaceholder: 'Brief description of this publication',
    },
    subscribe: {
        subscribe: 'Subscribe',
        subscribed: 'Subscribed',
        unsubscribe: 'Unsubscribe',
        subscribeAriaLabel: 'Subscribe to this publication',
        unsubscribeAriaLabel: 'Unsubscribe from this publication',
        loadingStatusAriaLabel: 'Loading subscription status',
    },
    recommend: {
        recommendAriaLabel: 'Recommend this article',
        removeRecommendAriaLabel: 'Remove recommendation',
    },
    pushNotifications: {
        /** Generic notification the service worker shows when a push payload is unreadable. */
        fallbackTitle: 'Mackinac',
        fallbackBody: 'You have new activity',
        pageTitle: 'Notifications',
        masterToggleLabel: 'Push notifications',
        masterToggleDescription:
            'Get notified in your browser for activity across your connected accounts.',
        unsupportedBody: 'Push notifications are not supported in this browser.',
        notConfiguredBody: 'Push notifications are not set up for this app yet.',
        blockedTitle: 'Notifications are blocked',
        blockedBody:
            'Notifications are blocked in your browser settings. Allow notifications for this site, then try again.',
        iosInstallHint:
            'On iPhone and iPad, add Mackinac to your home screen from the share menu to enable notifications.',
        accountsHeading: 'Connected accounts',
        typesHeading: 'Notify me about',
        typeLabels: {
            like: 'Likes',
            repost: 'Reposts',
            reply: 'Replies',
            mention: 'Mentions',
            quote: 'Quotes',
            follow: 'New followers',
            follow_request: 'Follow requests',
            poll: 'Poll results',
        },
        accountStatus: {
            registered: 'Notifications on',
            pending: 'Setting up…',
            needsReauth: 'Needs re-authorization for push',
            unsupportedInstance: "This server doesn't support push notifications",
            unavailable: "Mastodon push isn't available yet",
            error: 'Something went wrong — will retry automatically',
        },
        reauthorize: 'Re-authorize',
        reauthorizeRedirecting: 'Redirecting you to your Mastodon server to grant push permission…',
        banner: {
            title: 'Turn on notifications',
            body: "Get notified the moment someone likes, replies, or follows you — even when Mackinac isn't open.",
            enable: 'Enable',
            notNow: 'Not now',
        },
    },
    notifications: {
        liked: 'liked your post',
        likedRepost: 'liked your repost',
        boosted: 'boosted your post',
        reposted: 'reposted your post',
        repostedRepost: 'reposted your repost',
        followed: 'followed you',
        followRequested: 'requested to follow you',
        mentioned: 'mentioned you',
        replied: 'replied to your post',
        quoted: 'quoted your post',
        pollEnded: 'poll has ended',
        interacted: 'interacted with you',
        posted: 'posted',
        failedToLoad: 'Failed to load notifications',
        tryAgain: 'Try again',
        noNotifications: 'No notifications yet',
        noNotificationsSubtext: "When someone interacts with your posts, you'll see it here",
        grouped: {
            liked: (n: number) => `liked your post — ${n} ${n === 1 ? 'user' : 'users'}`,
            likedRepost: (n: number) => `liked your repost — ${n} ${n === 1 ? 'user' : 'users'}`,
            reposted: (n: number) => `reposted your post — ${n} ${n === 1 ? 'user' : 'users'}`,
            boosted: (n: number) => `boosted your post — ${n} ${n === 1 ? 'user' : 'users'}`,
            repostedRepost: (n: number) =>
                `reposted your repost — ${n} ${n === 1 ? 'user' : 'users'}`,
            followed: (n: number) => `${n} new ${n === 1 ? 'follower' : 'followers'}`,
            subscribedPost: (n: number) =>
                `${n} new ${n === 1 ? 'post' : 'posts'} from your subscriptions`,
        },
    },
    settings: {
        title: 'Settings',
        sectionAtmosphere: 'Atmosphere',
        sectionGeneral: 'General',
        sectionModeration: 'Moderation',
        appearance: 'Appearance',
        dataSources: 'Data sources',
        verification: 'Verification',
        moderation: 'Moderation',
        about: 'About',
        labs: 'Labs',
        developer: 'Developer',
        featureFlags: 'Feature flags',
        blockedUsers: 'Blocked users',
        developerFlags: 'Developer flags',
        rssFeeds: 'RSS feeds',
        notifications: 'Notifications',
    },
    about: {
        version: 'Version',
    },
    appearanceSettings: {
        theme: 'Theme',
        themeDescription: 'Choose light, dark, or match your system',
        darkModeLabel: 'Dark mode',
        lightModeLabel: 'Light mode',
        systemThemeLabel: 'System theme',
    },
    blockedUsers: {
        loading: 'Loading blocked users...',
        loadError: 'Failed to load blocked users. Please try again later.',
        empty: 'You have not blocked any users',
    },
    dataSources: {
        description: 'Choose which indexer serves your feed data',
    },
    featureFlags: {
        empty: 'No feature flags configured.',
    },
    labs: {
        description: 'Try experimental features. These may change or be removed.',
        empty: 'No labs features available.',
    },
    rssFeeds: {
        untitledFeed: 'Untitled feed',
        loading: 'Loading…',
        empty: 'No RSS feeds yet.',
    },
    feeds: {
        mixToggleLabel: 'Mix feeds into Following',
        mixToggleDescription: 'Show samples of your pinned feeds in the Following timeline',
        mixChooserTitle: 'Choose feeds to mix',
        mixToggleAriaLabel: 'Mix feeds into Following',
        followingRowLabel: 'Following',
        unpinTab: 'Unpin',
        pinTab: 'Pin',
        unpinTabAriaLabel: (name: string) => `Unpin ${name}`,
        pinTabAriaLabel: (name: string) => `Pin ${name}`,
        tabOrderSaved: 'Tab order saved',
        tabOrderSaveError: 'Failed to save tab order. Changes reverted.',
        blueskyFanoutError:
            'Tab order saved, but failed to sync with Bluesky. Your order will apply in Mackinac.',
        followingTabLocked: 'Following can be reordered like any other feed',
        mastodonTabsHeading: 'Mastodon tabs',
        mastodonTabsDescription: 'Drag to reorder. Hide a tab to move it to Hidden tabs.',
        mastodonTabsLoadingNotice:
            'Your lists and followed hashtags are still loading — they’ll appear here once ready.',
        mastodonHiddenTabsHeading: 'Hidden tabs',
        mastodonTabOrderSaveError: 'Failed to save tab changes. Please try again.',
        pinnedHeading: 'Pinned feeds',
        pinnedDescription: 'Drag to reorder. Tap the pin to move a feed to Saved feeds.',
        savedHeading: 'Saved feeds',
        manageFeeds: 'Manage feeds',
        removeConfirmLabel: 'Remove',
        removeConfirmBody: 'Are you sure you want to remove this feed?',
    },
    feedView: {
        fallbackName: 'Feed',
        backAriaLabel: 'Go back',
        byCreator: (handle: string) => `by @${handle}`,
    },
    errors: {
        somethingWentWrong: 'Something went wrong loading this page.',
        reload: 'Reload',
    },
    search: {
        recentsHeading: 'Recents',
        recentProfilesAriaLabel: 'Recent profiles',
        clearRecents: 'Clear',
        clearRecentsAriaLabel: 'Clear recent searches',
        feedsTab: 'Feeds',
        loadingState: (query: string) => `Searching for "${query}"...`,
        feedResultsHeading: 'Feeds',
        feedResultsAriaLabel: 'Feed search results',
        noFeedResults: (query: string) => `No feeds found for "${query}"`,
        publicationsResultsAriaLabel: 'Publication search results',
        suggestedPublicationsHeading: 'Publications',
        suggestedPublicationsAriaLabel: 'Publications',
        topStoriesHeading: 'Top stories',
        topStoriesAriaLabel: 'Top stories',
        topStoriesReadNews: 'Read news',
        hashtagsTab: 'Hashtags',
        hashtagResultsAriaLabel: 'Hashtag search results',
        noHashtagResults: (query: string) => `No hashtags found for "${query}"`,
        hashtagRecentUses: (count: number) => `${count} recent use${count === 1 ? '' : 's'}`,
        followButtonLabel: 'Follow',
        followingButtonLabel: 'Following',
        followAriaLabel: 'Follow this user',
        unfollowAriaLabel: 'Unfollow this user',
    },
    goToProfile: {
        buttonAriaLabel: 'Go to profile',
        sheetTitle: 'Go to profile',
        closeAriaLabel: 'Close',
        inputPlaceholder: '@handle, did:plc:…, or @user@server',
        inputAriaLabel: 'Enter an AT Protocol or ActivityPub handle, DID, or profile URL',
        submitButton: 'Go',
        recentHeading: 'Recent',
        favoritesHeading: 'Favorites',
        helperText: 'Works with AT Protocol handles and ActivityPub (Mastodon, etc.)',
        errorEmpty: 'Please enter a handle, DID, or profile URL',
        errorInvalid: 'Enter a valid handle, DID, or profile URL',
        recentEntryAriaLabel: (identifier: string) => `Go to profile ${identifier}`,
        favoriteEntryAriaLabel: (name: string) => `Go to profile ${name}`,
    },
    trending: {
        sectionTitle: 'Trending',
    },
    discoverFeeds: {
        sectionTitle: 'Feeds',
        sectionAriaLabel: 'Feeds',
        showMore: 'Show more',
        listTitle: 'Discover feeds',
        listUnavailable: 'Discover feeds is only available on Bluesky.',
        listEmpty: 'No suggested feeds right now.',
        loadingMore: 'Loading more…',
        error: "Couldn't load suggested feeds.",
        retry: 'Retry',
    },
    discoverPeople: {
        title: 'Discover people',
        unavailable: 'Discover people is only available on Bluesky.',
    },
    nostrThread: {
        loading: 'Loading…',
        eventNotFound: 'Event not found',
        noReplies: 'No replies yet',
    },
    hashtag: {
        loadingState: (tag: string) => `Loading posts for #${tag}...`,
        follow: 'Follow',
        unfollow: 'Unfollow',
        followAriaLabel: (tag: string) => `Follow #${tag} on Mastodon`,
        unfollowAriaLabel: (tag: string) => `Unfollow #${tag} on Mastodon`,
        followError: "Couldn't follow this hashtag. Please try again.",
        unfollowError: "Couldn't unfollow this hashtag. Please try again.",
    },
    storyFeed: {
        defaultHeading: 'Story',
        loadingState: 'Loading posts...',
        emptyState: (query: string) => `No posts found for "${query}"`,
        noMorePosts: 'No more posts',
        errorLoading: 'Failed to load posts. Please try again.',
        unavailableMessage: 'Story feed is only available on Bluesky.',
        postsAriaLabel: (headline: string) => `Posts for ${headline}`,
    },
    publicView: {
        signIn: 'Sign in',
        bannerMessage: 'Sign in to like, reply, and follow.',
        bannerCta: 'Sign in',
        signInToInteract: 'Sign in to interact with this post.',
    },
    tahquamenon: {
        howCanIHelp: 'How can I help?',
        loadingResponse: 'Loading response',
        thinking: 'Thinking…',
        messagePlaceholder: 'Ask about the Social Web',
        sendMessage: 'Send message',
        backToTahquamenon: 'Back to Tahquamenon',
        errorMessage: 'Something went wrong. Please try again.',
        featureUnavailable: "Tahquamenon isn't available for this account.",
        askSummarize: (ref: string) => `Summarize ${ref}`,
        askExplain: (ref: string) => `Explain ${ref}`,
        askAboutAuthor: (ref: string) => `Tell me about the author of ${ref}`,
        bridgedBadge: 'Bridged',
        viewProfile: 'View profile →',
        postUnavailable: 'Post unavailable',
        profileUnavailable: 'Profile unavailable',
        skillsMenu: 'Skills',
        trendingHeader: 'Trending now',
        trendingBlueskyLabel: 'Bluesky',
        trendingMastodonLabel: 'Mastodon',
        whatsTrendingChip: "What's trending?",
        fediverseResultsHeader: 'Fediverse accounts',
        followersLabel: 'followers',
        feedUnavailable: 'Feed unavailable',
        openFeed: 'Open feed',
        openArticle: 'Open article',
        articleUnavailable: 'Article unavailable',
        storyUnavailable: 'Story unavailable',
        listUnavailable: 'List unavailable',
        consentDeniedNotice:
            "You've set your AI preferences to deny inference, so Tahquamenon can't read your data.",
        consentDeniedAction: 'Allow inference in your AI preferences to use it.',
        consentUnverifiableNotice:
            "Couldn't verify your AI preferences right now, so Tahquamenon didn't run. Please try again in a moment.",
        consentSettingsLink: 'Open AI preferences',
    },
    aiPreferences: {
        savedToast: 'Saved. Apps that respect this lexicon will see your updated preferences.',
        pageTitle: 'AI preferences',
        description:
            "Tell the network how you'd like AI services to use your public data. Mackinac will always honor these settings. Other apps that respect this lexicon may also honor your choices, but compliance is voluntary. You can change these settings at any time.",
        sessionExpired: 'Your session has expired. Please sign in again.',
        conflictMessage:
            'Another client updated your preferences while you were editing. Review the current settings and save again.',
        saveError: "Couldn't save preferences — please try again.",
        saveButton: 'Save preferences',
        saving: 'Saving…',
        clearAll: 'Clear all',
        allowLabel: 'Allow',
        denyLabel: 'Deny',
        noPreferenceLabel: 'No preference',
        trainingLabel: 'Training',
        inferenceLabel: 'Inference',
        syntheticContentLabel: 'Synthetic content',
        embeddingLabel: 'Embedding',
        trainingDescription: 'Use my data to train, fine-tune, distill, or do RLHF on ML models',
        inferenceDescription:
            'Use my data at inference time for retrieval, RAG, or context injection (e.g. an LLM reading my posts to answer a question)',
        syntheticContentDescription:
            'Use my data to generate synthetic content or interactions derived from me',
        embeddingDescription: 'Use my data for vector embeddings or semantic indexing',
    },
    drafts: {
        title: 'Drafts',
        openTitle: 'Drafts',
        empty: 'No drafts yet.',
        loading: 'Loading drafts…',
        loadMore: 'Load more',
        noText: 'No text',
        loadError: "Couldn't load your drafts. Please try again.",

        // Row actions
        open: 'Open',
        delete: 'Delete',
        deleteTitle: 'Delete draft',
        importCopy: 'Import a copy',

        // Delete confirmation
        deleteConfirmTitle: 'Discard draft?',
        deleteConfirmBody: 'This draft will be permanently deleted.',
        deleteConfirmCancel: 'Cancel',
        deleteConfirmConfirm: 'Discard',
        deleteConfirmUnsupportedWarning:
            "This draft includes content Mackinac can't show here. Deleting it removes the whole draft, not just the text above.",

        // Lightweight metadata tags shown inline on a row (mirrors the official app's drafts list)
        tags: {
            quote: 'Quote post',
            morePosts: (count: number) => `${count} more ${count === 1 ? 'post' : 'posts'}`,
        },

        // Save
        saveDraft: 'Save draft',
        saving: 'Saving…',
        saveFailed: 'Failed to save draft',
        savePrompt: 'Save as draft?',
        savePromptSubtitle: 'You can finish writing it later.',

        // Attachment save (G1-g). Bluesky drafts do not store images or video, and the
        // composer closes on save, so the browser File objects are the user's only copy.
        attachmentsBlockSave: (count: number, kind: 'photo' | 'video') =>
            kind === 'video'
                ? 'Drafts cannot store your video.'
                : `Drafts cannot store your ${count === 1 ? 'photo' : `${count} photos`}.`,
        saveWithoutAttachments: (count: number, kind: 'photo' | 'video') =>
            kind === 'video'
                ? 'Save without video…'
                : `Save without ${count} ${count === 1 ? 'photo' : 'photos'}…`,
        discardAttachmentsTitle: (count: number, kind: 'photo' | 'video') =>
            kind === 'video'
                ? 'Save draft without your video?'
                : `Save draft without your ${count} ${count === 1 ? 'photo' : 'photos'}?`,
        discardAttachmentsBody: (count: number, kind: 'photo' | 'video') =>
            kind === 'video'
                ? 'It will be discarded and cannot be recovered.'
                : `${count === 1 ? 'It' : 'They'} will be discarded and cannot be recovered.`,
        discardAttachmentsConfirm: 'Save without them',
        discardAttachmentsCancel: 'Keep editing',

        // Import a copy (Lossless-Only Editing)
        importTitle: 'Import a copy?',
        importBody:
            "Mackinac will start a new draft from this one. The original is left untouched — edit or delete it in the Bluesky app. This copy won't include:",
        importConfirm: 'Import a copy',
        importCancel: 'Cancel',
        readOnlyReason: "Can't be edited here",

        // One entry per DroppedFeature kind
        dropped: {
            additionalPosts: (count: number) =>
                `${count} further ${count === 1 ? 'post' : 'posts'} in the thread`,
            images: (count: number) => `${count} ${count === 1 ? 'photo' : 'photos'}`,
            video: 'The attached video',
            selfLabels: 'Content warnings',
            languages: 'Language settings',
            threadgateRules: 'Reply settings',
            postgateRules: 'Quote settings',
            surplusExternalEmbeds: (count: number) =>
                `${count} extra ${count === 1 ? 'link card' : 'link cards'}`,
            surplusRecordEmbeds: (count: number) =>
                `${count} extra quoted ${count === 1 ? 'post' : 'posts'}`,
            unsupportedEmbedTarget: 'An embedded item Mackinac cannot quote',
            recordEmbedMissingCid: 'A quoted post that can no longer be identified',
            malformedExternalUri: 'A link card with an unreadable address',
            unknownFields: 'Newer draft features Mackinac does not understand yet',
        },

        // Quote hydration
        quoteLoading: 'Loading quoted post…',
        quoteFailed: "Couldn't load the quoted post.",
        quoteFailedDeleted: 'The quoted post is no longer available.',
        quoteConflictTitle: 'The quoted post has changed',
        quoteConflictBody:
            'It was edited after this draft was saved. Preview and publish the latest version, or drop the quote.',
        quoteUseLatest: 'Use latest version',
        quoteLoadWithout: 'Load without quote',
        quoteCancel: 'Cancel',
        quoteNotSavedNoCid: "This quote can't be saved to a draft, so the draft will be text-only.",

        // Link-card hydration
        linkLoading: 'Loading link preview…',
        linkFailed: "Couldn't load the link preview.",
        linkRedirectTitle: 'This link now points somewhere else',
        linkRedirectBody: (resolved: string) => `It resolves to ${resolved}.`,
        linkUseResolved: 'Use the new link',
        linkLoadWithout: 'Load without link',
        linkCancel: 'Cancel',

        // Freshness (accepted TOCTOU race, G1-h)
        staleTitle: 'This draft was updated somewhere else',
        staleBody: 'Reload it before continuing.',
        staleReload: 'Reload',
        staleCancel: 'Cancel',
        staleCheckFailed: "Couldn't check this draft for changes. Please try again.",
        alreadyDeleted: 'That draft was already deleted somewhere else.',

        // Publish cleanup
        cleanupFailed: "Posted. Couldn't remove the saved draft — you can delete it from Drafts.",

        // Errors
        limitReached: "You've reached the draft limit. Delete a draft to save a new one.",
        textTooLongCharacters: (over: number) =>
            `This draft is ${over} ${over === 1 ? 'character' : 'characters'} over the draft limit.`,
        textTooLongGraphemes: (over: number) =>
            `This draft is ${over} ${over === 1 ? 'character' : 'characters'} over the draft limit.`,
        deleteFailed: "Couldn't delete that draft. Please try again.",
    },
    contentWarnings: {
        contentWarning: 'Content warning',
        sensitiveContent: 'Sensitive content',
    },
    addToMastodonList: {
        title: (targetHandle: string) => `Add @${targetHandle} to list`,
        noListsYet: "You don't have any lists yet.",
        resolveError: "Couldn't find this account. Please try again.",
        listsError: "Couldn't load your lists. Please try again.",
        membershipError: "Couldn't check list membership. Please try again.",
        retry: 'Retry',
        notFollowingNotice: 'Mastodon only allows adding people you follow to lists.',
        addFailed: "Couldn't update this list. Please try again.",
    },
    altText: {
        title: 'Add alt text',
        previewAlt: 'Preview',
        fieldLabel: 'Descriptive alt text',
        placeholder: 'Alt text',
    },
    lists: {
        noCurateListsYet: "You don't have any curate lists yet.",
        listNameFieldLabel: 'List name',
        descriptionOptionalLabel: 'Description (optional)',
        whatIsThisListAboutPlaceholder: 'What is this list about?',
        addPeopleTitle: 'Add people to list',
        added: 'Added',
        noUsersFound: 'No users found',
        searchForPeopleToAdd: 'Search for people to add',
        searchByHandleOrNamePlaceholder: 'Search by handle or name…',
        addPeople: 'Add people',
        nameLabel: 'Name',
        listNamePlaceholder: 'List name',
        noListSpecified: 'No list specified.',
        noMembersYet: 'No members yet',
        editListTitle: 'Edit list',
        titleLabel: 'Title',
        listTitlePlaceholder: 'List title',
        optionalDescriptionPlaceholder: 'Optional description',
    },
} as const
